import { useState, useCallback, useEffect } from 'react';
import {
  useGetAdminReportsQuery,
  useUpdateReportStatusMutation,
  useDeleteThreadMutation,
  useDeleteReplyMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useAdminUser } from './useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

const PAGE_SIZE = 30;

export const useReportReview = () => {
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const { showSuccess, showError } = useToast();

  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('pending');
  const [page, setPage] = useState(0);
  const [allReports, setAllReports] = useState<CorkBoardReport[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [lastSyncedReportsData, setLastSyncedReportsData] = useState<unknown>(undefined);

  useEffect(() => {
    setPage(0);
    setAllReports([]);
    setHasNextPage(false);
  }, [statusFilter, selectedPropertyId]);

  const {
    data: reportsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAdminReportsQuery(
    {
      propertyId: selectedPropertyId!,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      pageSize: PAGE_SIZE,
    },
    { skip: !selectedPropertyId },
  );

  // Sync during render (not useEffect) so allReports updates in the same commit RTK
  // exposes new data — avoids a one-frame empty-state flash after isLoading flips false.
  if (reportsData && reportsData !== lastSyncedReportsData) {
    setLastSyncedReportsData(reportsData);
    const incoming = reportsData.reports ?? [];

    if (page === 0) {
      setAllReports(incoming);
    } else {
      setAllReports((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const unique = incoming.filter((r) => !existingIds.has(r.id));
        return [...prev, ...unique];
      });
    }

    const { currentPage, totalPages } = reportsData.pagination;
    setHasNextPage(currentPage < totalPages - 1);
  }

  const [updateReportStatusMutation, { isLoading: isUpdatingStatus }] = useUpdateReportStatusMutation();
  const [deleteThreadMutation, { isLoading: isDeletingThread }] = useDeleteThreadMutation();
  const [deleteReplyMutation, { isLoading: isDeletingReply }] = useDeleteReplyMutation();

  const isMutating = isUpdatingStatus || isDeletingThread || isDeletingReply;

  const loadMore = useCallback(() => {
    if (!isFetching && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasNextPage]);

  const handleRefetch = useCallback(() => {
    setPage(0);
    setAllReports([]);
    setHasNextPage(false);
    refetch();
  }, [refetch]);

  const totalCount = reportsData?.pagination?.totalCount ?? 0;

  const handleRemoveContent = useCallback(
    async (report: CorkBoardReport) => {
      try {
        if (report.contentType === 'thread' && report.threadId) {
          await deleteThreadMutation(report.threadId).unwrap();
        } else if (report.contentType === 'reply' && report.replyId) {
          await deleteReplyMutation({
            replyId: report.replyId,
            threadId: report.parentThreadId ?? '',
          }).unwrap();
        }

        await updateReportStatusMutation({
          reportId: report.id,
          status: 'resolved',
          resolution: 'removed',
        }).unwrap();

        showSuccess('Content removed and report resolved');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to remove content');
        return false;
      }
    },
    [deleteThreadMutation, deleteReplyMutation, updateReportStatusMutation, showSuccess, showError],
  );

  const handleDismissReport = useCallback(
    async (reportId: string) => {
      try {
        await updateReportStatusMutation({
          reportId,
          status: 'dismissed',
          resolution: 'dismissed',
        }).unwrap();
        showSuccess('Report dismissed');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to dismiss report');
        return false;
      }
    },
    [updateReportStatusMutation, showSuccess, showError],
  );

  const handleWarnUser = useCallback(
    async (reportId: string) => {
      try {
        await updateReportStatusMutation({
          reportId,
          status: 'resolved',
          resolution: 'warned',
        }).unwrap();
        showSuccess('Warning noted. Report resolved.');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to warn user');
        return false;
      }
    },
    [updateReportStatusMutation, showSuccess, showError],
  );

  return {
    reports: allReports,
    isLoading,
    isFetching,
    isMutating,
    isUpdatingStatus,
    isDeletingThread,
    isDeletingReply,
    error,
    selectedPropertyId,
    statusFilter,
    setStatusFilter,
    refetch: handleRefetch,
    hasNextPage,
    loadMore,
    totalCount,
    handleRemoveContent,
    handleDismissReport,
    handleWarnUser,
  };
};
